import { type ReactElement } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
export interface LamaMaturityTrendChartPoint {
  readonly label: string;
  /** Overall maturity in PERCENT (0–100). */
  readonly overall: number;
  /**
   * The wave's own date (`YYYY-MM-DD`). The AXIS stays the wave code — a date
   * per tick crowds three waves into unreadable strings — but the tooltip says
   * WHEN, because "W3" alone does not tell a reader whether the last point is
   * last month or last year.
   */
  readonly date?: string;
}

/**
 * Overall-maturity trend across waves — a line on a fixed `[0, max]` percent
 * scale (2026-09-10 ADR-0027 amendment; it was `[1, 5]`).
 * Ported from lama-feature's `LamaReportTrendChart` for the dashboard widget
 * body (data-blind: points arrive as props; no clock, no fetch). Keeps the
 * `role="img"` + data-derived aria summary pattern; animation disabled for
 * deterministic tests.
 */
export function LamaMaturityTrendChart({
  data,
  max = 100,
  height = 220,
}: {
  /** One point per wave (oldest → newest). */
  readonly data: readonly LamaMaturityTrendChartPoint[];
  readonly max?: number;
  readonly height?: number;
}): ReactElement {
  const latest = data.length ? data[data.length - 1] : null;
  const summary =
    `Maturity trend across ${data.length} wave${data.length === 1 ? "" : "s"}` +
    (latest ? `; latest ${Math.round(latest.overall)}%.` : ".");
  const dates = new Map<string, string>();
  for (const point of data) {
    if (point.date !== undefined) dates.set(point.label, point.date);
  }

  return (
    <div role="img" aria-label={summary} className="w-full" style={{ height }}>
      <div aria-hidden="true" className="h-full w-full">
        {/* Kept hand-built: recharts chart marks, which no design-system part draws. */}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={[...data]}
            /* `left: 0`, not `-16`: the axis reserves 40px and a `100%` tick is
               about 26px at 11px, so a negative gutter clipped the top label
               against the card's own padding. */
            margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--pb-border))"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--pb-border))" }}
            />
            <YAxis
              domain={[0, max]}
              /* The SAME five ticks as the participation chart beside it. The
                 two used to disagree (20s here, 25s there), so a reader
                 comparing them had to re-read both scales. */
              ticks={[0, 0.25, 0.5, 0.75, 1].map((fraction) => fraction * max)}
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              tickLine={false}
              axisLine={false}
              width={40}
              unit="%"
            />
            <RechartsTooltip
              cursor={{ stroke: "hsl(var(--pb-border))", strokeWidth: 1 }}
              contentStyle={{
                fontSize: 12,
                /* `backgroundColor`, not the `background` shorthand: recharts
                   sets its own `backgroundColor: '#fff'` first and the two only
                   resolve in our favour by key order. */
                backgroundColor: "hsl(var(--pb-card))",
                border: "1px solid hsl(var(--pb-border))",
                borderRadius: 8,
                color: "hsl(var(--pb-text))",
              }}
              /* recharts colours the value row from the series stroke and
                 ignores `contentStyle.color` for it. */
              itemStyle={{ color: "hsl(var(--pb-text))" }}
              labelStyle={{ color: "hsl(var(--pb-text-muted))", fontSize: 11 }}
              labelFormatter={(label: unknown) => {
                const code = String(label);
                const date = dates.get(code);
                return date ? `${code} · ${date}` : code;
              }}
              formatter={(value: number) => [
                `${Math.round(value)}%`,
                "Overall",
              ]}
            />
            <Line
              type="monotone"
              dataKey="overall"
              /* Cyan, like the participation bars and the radar: LAMA's own
                 accent. Teal is the Analytics hue and put two charts on one
                 report in two different colours for one campaign. */
              stroke="hsl(var(--pb-cyan))"
              strokeWidth={2}
              dot={{ r: 3, fill: "hsl(var(--pb-cyan))" }}
              /* recharts defaults an active dot's ring to a hardcoded `#fff`,
                 which is a bright artefact on dark and invisible on light. */
              activeDot={{
                r: 5,
                stroke: "hsl(var(--pb-card))",
                strokeWidth: 2,
              }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
